import {
  Box,
  Button,
  HStack,
  Icon,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Check, Plus, X } from 'lucide-react'
import React from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Modal } from '@mangovoice/mango-ui'

import type {
  AudioTrackSummary,
  MusicOnHoldApi,
} from '../api/musicOnHoldApi.types'
import {
  useAddTrackToPlaylist,
  useGetAudioTracks,
  useGetPlaylist,
  useRemoveTrackFromPlaylist,
} from '../queries/musicOnHoldQueries'
import { type MusicOnHoldTranslateFn, text } from '../types/i18n.types'
import { TrackPlayButton } from './TrackPlayButton'

export interface ManageTracksModalProps {
  api: MusicOnHoldApi
  pbxId: number
  playlistId: number
  open: boolean
  onClose: () => void
  t?: MusicOnHoldTranslateFn
  toast?: ToastLike
}

export const ManageTracksModal: React.FC<ManageTracksModalProps> = ({
  api,
  pbxId,
  playlistId,
  open,
  onClose,
  t,
  toast,
}) => {
  const playlistQuery = useGetPlaylist(api, open ? playlistId : undefined)
  const tracksQuery = useGetAudioTracks(api, open ? pbxId : undefined)
  const addMutation = useAddTrackToPlaylist(api, pbxId)
  const removeMutation = useRemoveTrackFromPlaylist(api, pbxId)

  const assignedTracks = playlistQuery.data?.audio_tracks ?? []
  const assignedIds = new Set(assignedTracks.map((tr) => tr.id))
  const allTracks = tracksQuery.data ?? []
  const availableTracks = allTracks.filter((tr) => !assignedIds.has(tr.id))

  const isLoading = playlistQuery.isLoading || tracksQuery.isLoading
  const isMutating = addMutation.isPending || removeMutation.isPending

  const notify = (fallback: string, err: unknown) => {
    toast?.create({
      title: fallback,
      description: err instanceof Error ? err.message : undefined,
      type: 'error',
    })
  }

  const handleAdd = (track: AudioTrackSummary) => {
    addMutation.mutate(
      { playlistId, audioTrackId: track.id },
      {
        onError: (err) =>
          notify(
            text(
              t,
              'musicOnHold.manageTracks.addFailed',
              'Failed to add track',
            ),
            err,
          ),
      },
    )
  }

  const handleRemove = (track: AudioTrackSummary) => {
    removeMutation.mutate(
      { playlistId, audioTrackId: track.id },
      {
        onError: (err) =>
          notify(
            text(
              t,
              'musicOnHold.manageTracks.removeFailed',
              'Failed to remove track',
            ),
            err,
          ),
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={(e) => !e.open && onClose()}
      title={text(t, 'musicOnHold.manageTracks.title', 'Manage Tracks')}
      maxWidth="2xl"
      footer={
        <Button borderRadius="lg" variant="outline" onClick={onClose}>
          <Icon as={Check} />
          {text(t, 'musicOnHold.manageTracks.done', 'Done')}
        </Button>
      }
    >
      {isLoading ? (
        <HStack justify="center" py={8}>
          <Spinner />
        </HStack>
      ) : (
        <VStack gap={6} align="stretch">
          <Box>
            <Text fontWeight="semibold" mb={2}>
              {text(
                t,
                'musicOnHold.manageTracks.inPlaylistTitle',
                'In this playlist',
              )}
            </Text>
            {assignedTracks.length === 0 ? (
              <Text fontSize="sm" color="fg.muted" fontStyle="italic">
                {text(
                  t,
                  'musicOnHold.manageTracks.noneAssigned',
                  'No tracks have been added to this playlist yet.',
                )}
              </Text>
            ) : (
              <Table.Root size="sm">
                <Table.Body>
                  {assignedTracks.map((track) => (
                    <Table.Row key={track.id}>
                      <Table.Cell>{track.name}</Table.Cell>
                      <Table.Cell textAlign="end" width="1%">
                        <TrackPlayButton
                          api={api}
                          pbxId={pbxId}
                          trackUuid={track.uuid}
                          t={t}
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="end" width="1%">
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="error"
                          borderRadius="md"
                          disabled={isMutating}
                          onClick={() => handleRemove(track)}
                        >
                          <Icon as={X} />
                          {text(t, 'musicOnHold.manageTracks.remove', 'Remove')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Box>

          <Box>
            <Text fontWeight="semibold" mb={2}>
              {text(
                t,
                'musicOnHold.manageTracks.availableTitle',
                'Available tracks',
              )}
            </Text>
            {availableTracks.length === 0 ? (
              <Text fontSize="sm" color="fg.muted" fontStyle="italic">
                {allTracks.length === 0
                  ? text(
                      t,
                      'musicOnHold.manageTracks.noTracksUploaded',
                      'No audio tracks have been uploaded for this PBX.',
                    )
                  : text(
                      t,
                      'musicOnHold.manageTracks.allAdded',
                      'All available tracks are already in this playlist.',
                    )}
              </Text>
            ) : (
              <Table.Root size="sm">
                <Table.Body>
                  {availableTracks.map((track) => (
                    <Table.Row key={track.id}>
                      <Table.Cell>{track.name}</Table.Cell>
                      <Table.Cell textAlign="end" width="1%">
                        <TrackPlayButton
                          api={api}
                          pbxId={pbxId}
                          trackUuid={track.uuid}
                          t={t}
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="end" width="1%">
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="brand"
                          borderRadius="md"
                          disabled={isMutating}
                          onClick={() => handleAdd(track)}
                        >
                          <Icon as={Plus} />
                          {text(t, 'musicOnHold.manageTracks.add', 'Add')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Box>
        </VStack>
      )}
    </Modal>
  )
}
